<template>
  <v-card>

    <error-dialog :status="saveStatus" />

    <dialog-scroll-lock width="300" v-model="showReset">
      <v-card>
        <v-card-title>Are you sure you want to reset the RSVP form?</v-card-title>
        <v-list class="primary--text" dense>
          <v-list-item class="simple px-6" @click="reset(true)">
            <div>
              <div>Reset to default form</div>
              <div class="text--label-active">Includes inputs for counting attendees</div>
            </div>
            <v-icon v-html="mdiChevronRight" />
          </v-list-item>
          <v-list-item class="simple px-6" @click="reset(false)">
            <div>
              <div>Reset to empty form</div>
              <div class="text--label-active">Fully customizable</div>
            </div>
            <v-icon v-html="mdiChevronRight" />
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-btn text color="orange" @click="showReset = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </dialog-scroll-lock>

    <v-menu
      v-if="$refs.opts && $refs.opts[ staged.inputs.indexOf(selectedInput) ]"
      right nudge-top="8" offset-x
      :close-on-content-click="false"
      :activator="$refs.opts[ staged.inputs.indexOf(selectedInput) ].$el"
      :value="true"
      @input="selectedInput = null"
    >
      <v-list v-if="selectedInput" dense>
        <div v-if="(selectedInput.type == 'INT') && !selectedInput.lock?.includes('range')" class="d-flex px-2">
          <int-field
            class="ma-2"
            label="Min value" hide-stepper
            :rules="[ v => parseInt(v) < selectedInput.max ]"
            v-model="selectedInput.min"
          />
          <int-field
            class="ma-2"
            label="Max value" hide-stepper
            :rules="[ v => parseInt(v) > selectedInput.min ]"
            v-model="selectedInput.max"
          />
        </div>

        <v-list-item
          v-for="[ key, subtitle ] in options" :key="key"
          class="py-2"
          :disabled="!!selectedInput.lock?.includes(key)"
          @click="$set(selectedInput, key, !selectedInput[key])"
        >
          <v-checkbox
            class="my-0 py-0 mr-2"
            readonly hide-details
            :disabled="!!selectedInput.lock?.includes(key)"
            :input-value="selectedInput[key]"
          />
          <div>
            <div style="line-height: 1.2em; text-transform: capitalize;">{{key}}</div>
            <div class="text--label-active">{{subtitle}}</div>
          </div>
        </v-list-item>
        <v-list-item
          v-if="!selectedInput.lock?.includes('delete')"
          @click="deleteSection(staged.inputs.indexOf(selectedInput)).then(() => selectedInput = null)"
        >
          <v-icon class="mr-4" color="warning" v-html="mdiDelete" />
          <div class="warning--text">Delete</div>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-btn class="absolute" style="top: 12px; right: 12px;" icon @click="tryClose">
      <v-icon v-html="mdiClose" />
    </v-btn>

    <v-card-title>RSVP form</v-card-title>
    <v-card-subtitle>Design your own custom RSVP form<!--or choose from one of our pre-built forms for common occasions--></v-card-subtitle>

    <v-divider />

    <div v-if="staged.inputs?.length" class="pl-9">
    <draggable
      style="min-height: 80px;"
      draggable=".draggable"
      handle=".draggable-handle"
      ghost-class="draggable-ghost"
      :move="e => !e.related.classList.contains('draggable-disabled')"
      v-model="staged.inputs"
    >
      <div
        v-for="input, i in staged.inputs"
        class="py-6 draggable d-flex align-center relative"
        v-bind:class="{ 'draggable-disabled': !!input.lock?.includes('move') }"
      >

        <!-- 
        <div
          v-if="input.conditional"
          class="absolute"
          style="top: 24px; bottom: 24px; left: -10px; width: 16px; border: thin dashed #9e9e9e; border-right: none;"
        />
        -->

        <div class="absolute d-flex flex-column btn-group btn-group-vertical" style="left: -28px;">
          <!--
          <v-btn
            v-blur
            class="btn-group-bottom"
            :color="isEditingItem(i) ? 'primary' : null"
            fab small
            @mousedown="onMouseDownItem(i)"
          >
            <v-icon :color="isEditingItem(i) ? null : 'primary'" v-html="mdiPencil" />
          </v-btn>
          -->
          <v-btn v-if="!input.lock?.includes('move')" class="draggable-handle" style="cursor: grab;" fab small>
            <v-icon color="primary" v-html="mdiArrowSplitHorizontal" />
            <span class="btn-tooltip right">Drag to rearrange</span>
          </v-btn>
          <v-btn
            ref="opts"
            v-show="!input.root"
            fab small
            @click="
              () => {
                const wasOpen = selectedInput === input;
                selectedInput = null;
                if (!wasOpen)
                  $nextTick(() => selectedInput = input);
              }
            "
          >
            <v-icon v-bind:style="{ transform: selectedInput === input ? 'rotate(90deg)' : null }" color="primary" v-html="mdiDotsHorizontal" />
            <span class="btn-tooltip right">Settings</span>
          </v-btn>
        </div>

        <div v-if="input.conditional" class="pl-5">
          <div
            class="absolute"
            style="top: 12px; bottom: 12px; width: 16px; border: thin dashed #9e9e9e; border-right: none;"
          />
        </div>

        <div class="px-5" style="width: 100%;">
          <content-editable-div
            ref="editable-title"
            class="title preserve-newlines"
            v-bind:class="{ 'mb-1': !input.lock?.includes('title'), 'success--text': input.public || input.root }"
            :disabled="!!input.lock?.includes('title')"
            :error="!input.lock?.includes('title') && input.dirty && input.errors.title"
            :placeholder="input.type == 'HEADER' ? 'Title' : 'RSVP question'"
            v-model="input.title"
          >
            <template v-slot:suffix>
              <span v-if="input.required" class="error--text" style="vertical-align: top;"> *</span>
            </template>
          </content-editable-div>
          <v-card
            v-if="!input.lock?.includes('title') && input.dirty && input.errors.title"
            class="text--label-active absolute error--text"
            style="z-index: 1; padding: 0 1px; margin: -9px 0 0 2px; pointer-events: none;"
            flat
          >
            {{input.errors.title}}
          </v-card>

          <content-editable-div
            v-if="!input.lock?.includes('subtitle') || input.subtitle"
            ref="editable-subtitle"
            class="pa-0 v-card__subtitle preserve-newlines mb-2"
            style="color: rgba(0, 0, 0, 0.6);"
            placeholder="Details"
            :disabled="!!input.lock?.includes('subtitle')"
            :error="input.dirty && input.errors.subtitle"
            v-model="input.subtitle"
          />

          <div v-if="input.type == 'RADIO'">
            <v-radio-group v-if="input.lock" class="mt-1 py-0" readonly hide-details :row="input.row">
              <v-radio v-for="choice, j in input.choices" :key="i + '.' + j" disabled :label="choice" />
            </v-radio-group>
            <template v-else>
              <div v-for="choice, j in input.choices" class="d-flex align-center relative" style="margin-left: -6px;">
                <v-btn
                  icon color="warning"
                  :disabled="input.choices.length <= 2"
                  @click="input.lock?.includes('choices') || input.choices.splice(j, 1)"
                >
                  <v-icon v-html="mdiMinus" />
                </v-btn>
                <div class="flex-grow-1" @click.stop.prevent>
                  <content-editable-div
                    :ref="'editable-option-' + i"
                    class="px-1"
                    style="color: rgba(0, 0, 0, 0.6);"
                    :placeholder="'Option ' + (j + 1)"
                    :error="input.dirty && input.errors.choices && input.errors.choices[j]"
                    v-model="input.choices[j]"
                  />
                </div>
                <v-card
                  v-if="input.dirty && input.errors.choices && input.errors.choices[j]"
                  class="text--label-active absolute error--text"
                  style="z-index: 1; padding: 0 2px; left: 38px; bottom: -3px; pointer-events: none;"
                  flat
                >
                  {{input.errors.choices[j]}}
                </v-card>
              </div>
            </template>
            <v-btn
              v-if="!input.lock?.includes('choices')"
              style="margin-left: -6px;"
              color="primary" icon
              @click="
                () => {
                  const k = input.choices.push('') - 1;
                  $nextTick(() => $refs['editable-option-' + i][k].$refs.content.focus());
                }
              "
            >
              <v-icon v-html="mdiPlus" />
            </v-btn>
          </div>

          <div v-else-if="input.type == 'ATTENDEE_SEPARATE_COUNTS'">
            <int-field class="mb-3" label="Adults" style="width: 180px;" disabled />
            <br>
            <int-field label="Children" style="width: 180px;" disabled />
          </div>

          <int-field v-else-if="input.type == 'INT'" class="d-flex" style="width: 180px;" disabled />

          <v-textarea
            v-else-if="input.type == 'TEXT'"
            class="mt-2"
            outlined hide-details auto-grow disabled
            :placeholder="input.placeholder"
          />

          <v-text-field
            v-else-if="input.type == 'TEXT_SHORT'"
            class="mt-2"
            outlined dense hide-details disabled
            :placeholder="input.placeholder"
          />

          <div v-else-if="input.type == 'ATTENDEE_NAMES'" class="mt-n1">
            <div v-for="icon in (input.separate_counts ? [ mdiHumanMale, iconPathChild ] : [ null ])" class="my-1 d-flex align-center">
              <v-text-field class="mr-3" dense outlined hide-details placeholder="Name" disabled>
                <template v-slot:append>
                  <v-btn v-if="icon" style="margin-top: -2px;" small icon disabled>
                    <v-icon v-html="icon" />
                  </v-btn>
                </template>
              </v-text-field>
              <v-btn class="mr-n2"  icon disabled>
                <v-icon v-html="mdiMinus" />
              </v-btn>
            </div>
            <div class="text-right mr-n2">
              <v-btn icon disabled>
                <v-icon v-html="mdiPlus" />
              </v-btn>
            </div>
          </div>

          <!-- <div v-if="input.dirty" class="absolute text--label-active error--text">
            <div v-for="e in input.errors">{{e}}</div>
          </div> -->

        </div>
      </div>
    </draggable>
    </div>

    <v-card-actions>

      <!--
      <v-menu bottom offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" class="Xmt-n6 pl-3 pr-2" text color="primary">
            Occasions
            <v-icon v-html="mdiMenuDown" />
          </v-btn>
        </template>
        <v-list class="py-0" dense>
          <v-list-item @click="selectOccasion('DEFAULT')">
            <v-list-item-icon class="mr-3"><v-icon v-html="mdiTextShort" /></v-list-item-icon>
            <v-list-item-title>Party</v-list-item-title>
          </v-list-item>
          <v-list-item @click="selectOccasion('POTLUCK')">
            <v-list-item-icon class="mr-3"><v-icon v-html="mdiTextShort" /></v-list-item-icon>
            <v-list-item-title>Potluck</v-list-item-title>
          </v-list-item>
          <v-list-item @click="selectOccasion('DEFAULT')">
            <v-list-item-icon class="mr-3"><v-icon v-html="mdiTextShort" /></v-list-item-icon>
            <v-list-item-title>Baby shower</v-list-item-title>
          </v-list-item>
          <v-list-item @click="selectOccasion('MOVIE_NIGHT')">
            <v-list-item-icon class="mr-3"><v-icon v-html="mdiTextShort" /></v-list-item-icon>
            <v-list-item-title>Movie night</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      -->

      <v-menu right offset-x>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" class="ml-n2" fab small>
            <v-icon color="primary" v-html="mdiPlus" />
            <span class="btn-tooltip right">Add section…</span>
          </v-btn>
        </template>
        <v-list dense>
          <div class="px-3 mb-1 text--label-active">Add section</div>
          <v-list-item
            v-if="types.includes('HEADER')"
            class="simple primary--text"
            @click="addSection({ title: '', subtitle: '', type: 'HEADER', lock: [ 'public', 'required' ] })"
          >
            <v-icon v-html="mdiTextShort" />
            <div>Title / details</div>
            <!--
            <v-list-item-icon class="mr-3"><v-icon v-html="mdiTextShort" /></v-list-item-icon>
            <v-list-item-title>Title / details</v-list-item-title>
            -->
          </v-list-item>
          <v-list-item
            v-if="types.includes('RADIO')"
            class="simple primary--text"
            @click="addSection({ title: '', subtitle: '', type: 'RADIO', choices: [ '', '', '' ], private: true })"
          >
            <v-icon v-html="mdiRadioboxMarked" />
            <div>Multiple choice</div>
          </v-list-item>
          <v-list-item
            v-if="types.includes('INT')"
            class="simple primary--text"
            @click="addSection({ title: '', subtitle: '', type: 'INT', private: true, min: 0, max: 100 })"
          >
            <v-icon v-html="mdiNumeric" />
            <div>Number</div>
          </v-list-item>
          <v-list-item
            v-if="types.includes('TEXT')"
            class="simple primary--text"
            @click="addSection({ title: '', subtitle: '', type: 'TEXT', private: true })"
          >
            <v-icon v-html="mdiCardTextOutline" />
            <div>Text</div>
          </v-list-item>
          <v-list-item
            v-if="types.includes('EMAIL')"
            class="simple primary--text"
            @click="addSection({ title: '', subtitle: '', type: 'EMAIL', private: true })"
          >
            <v-icon v-html="mdiAt" />
            <div>Email</div>
          </v-list-item>
        </v-list>
      </v-menu>

    </v-card-actions>

    <v-divider />

    <v-card-actions>
      <v-btn text color="warning" @click="showReset = true">Reset…</v-btn>
      <v-spacer />
      <v-btn text @click="tryClose">Close</v-btn>
      <v-btn v-bind:class="{ pulse: dirty }" text color="primary" :disabled="!dirty" @click="save">Update</v-btn>
    </v-card-actions>

  </v-card>
</template>


<style scoped>
.title {
  line-height: 1.2em;
}
</style>


<script>
import ContentEditableDiv from '@/components/ContentEditableDiv.vue';
import ContentEditableInline from '@/components/ContentEditableInline.vue';
import IntField from '@/components/IntField.vue';
import { getFixedInputs } from '@/utils/event-rsvp-form.js';
import Draggable from 'vuedraggable';
import iconPathChild from 'raw-loader!@/assets/icon-path-child.txt';
import { mdiClose, mdiArrowSplitHorizontal, mdiDelete, mdiPlus, mdiRadioboxMarked, mdiNumeric, mdiTextShort, mdiCardTextOutline, mdiPencil, mdiAt, mdiMinus, mdiHumanMale, mdiDotsHorizontal, mdiChevronRight } from '@mdi/js';


function isBlank(x) {
  return !x || !/\S/.test(x);
}


function getErrors(input, titleCounts) {
  if (input.type == 'HEADER')
    return (isBlank(input.title) && isBlank(input.subtitle)) ? { title: 'Cannot be blank', subtitle: true } : {};
  const errors = {};
  if (isBlank(input.title))
    errors.title = 'Cannot be blank';
  else if (titleCounts[input.title] > 1)
    errors.title = 'Duplicate question';
  if (input.type == 'RADIO') {
    errors.choices = {};
    const choiceCounts = {};
    for (const choice of input.choices) {
      if (!isBlank(choice)) {
        choiceCounts[choice] ||= 0
        choiceCounts[choice]++;
      }
    }
    for (let i = 0; i < input.choices.length; i++)
      if (isBlank(input.choices[i]))
        errors.choices[i] = 'Cannot be blank';
      else if (choiceCounts[ input.choices[i] ] > 1)
        errors.choices[i] = 'Duplicate option';
    if (!Object.values(errors.choices).length)
      delete errors.choices;
  }
  return errors;
}


export default {
  name: 'form-builder',

  components: {
    ContentEditableDiv,
    ContentEditableInline,
    Draggable,
    IntField
  },

  props: {
    value: Object,
    types: { type: Array, default: () => [ 'HEADER', 'RADIO', 'INT', 'TEXT' ] }
  },

  data() {
    return {
      selectedInput: null,
      staged: null,
      showReset: false,
      saveStatus: this.$track('save')
    };
  },

  computed: {
    dirty() {
      return !_.isEqual(this.value, this.staged);
    },

    hasRoot() {
      return this.staged.inputs.find(x => x.root);
    },

    options() {
      return [
        [ 'public', 'Show responses in the public guest list' ],
        ...(this.hasRoot ? [ [ 'conditional', 'Only show this section for people who can attend' ] ] : []),
        [ 'required', 'Make sure respondents answer this question' ]
      ];
    }
  },

  created() {
    Object.assign(this, { iconPathChild, mdiClose, mdiArrowSplitHorizontal, mdiDelete, mdiPlus, mdiRadioboxMarked, mdiNumeric, mdiTextShort, mdiCardTextOutline, mdiPencil, mdiAt, mdiMinus, mdiHumanMale, mdiDotsHorizontal, mdiChevronRight });
  },

  methods: {
    async tryClose() {
      if (this.dirty) {
        const r = await this.$confirm({ title: 'Keep changes?', buttons: [ 'Cancel', 'No', 'Yes' ] });
        if (r == 'Cancel')
          return;
        if (r == 'Yes')
          await this.save();
      }
      this.$emit('close');
    },

    async reset(toDefault) {
      if (toDefault)
        this.staged.inputs = [ ...getFixedInputs(this.value), { type: 'TEXT', title: 'Note to host', public: false } ];
      else
        this.staged.inputs = [];
      this.showReset = false;
    },

    validate() {
      const titleCounts = {};
      for (const { title } of this.staged.inputs) {
        if (!isBlank(title)) {
          titleCounts[title] ||= 0
          titleCounts[title]++;
        }
      }
      let valid = true;
      for (const input of this.staged.inputs) {
        const errors = getErrors(input, titleCounts);
        if (!_.isEqual(input.errors, errors))
          this.$set(input, 'errors', errors);
        if (Object.values(errors).length)
          valid = false;
      }
      return valid;
    },

    getEditables(i) {
      const editables = [];
      for (const key of [ 'editable-title', 'editable-subtitle' ])
        if (this.$refs[key] && this.$refs[key][i])
          editables.push(this.$refs[key][i].$refs.content);
      for (let j = 0; this.$refs['editable-option-' + i] && this.$refs['editable-option-' + i][j]; j++)
        editables.push(this.$refs['editable-option-' + i][j].$refs.content);
      return editables;
    },

    onMouseDownItem(i) {
      const editables = this.getEditables(i);
      const j = editables.findIndex(x => x === this.focused);
      const next = editables[j + 1];
      if (next)
        setTimeout(() => next.focus());
    },

    isEditingItem(i) {
      for (const editable of this.getEditables(i))
        if (this.focused === editable)
          return true;
      return false;
    },

    async addSection(x) {
      const i = this.staged.inputs.push(x) - 1;
      await this.$nextTick();
      this.onMouseDownItem(i);
    },

    async deleteSection(i) {
      if (this.staged.inputs[i].title) {
        const r = await this.$confirm({ title: 'Are you sure?', msg: 'Are you sure you want to delete this section?' });
        if (r == 'No')
          return;
      }
      this.staged.inputs.splice(i, 1);
    },

    async save() {
      if (!this.staged.inputs.filter(x => x.type != 'HEADER').length)
        throw new VisibleError('RSVP form must include at least one input');
      for (const input of this.staged.inputs)
        this.$set(input, 'dirty', true);
      if (!this.validate())
        throw new VisibleError('Please fix the highlighted errors before continuing');
      this.$emit('input', this.staged);
      this.$emit('close');
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.staged = _.cloneDeep(v);
      }
    },

    staged: {
      deep: true,
      handler(v) {
        this.validate();
      }
    }
  }
}
</script>
